/**
 * Hosting dashboard hooks — TanStack Query v5 for host stats, bookings, earnings, calendar.
 *
 * Optimistic update on setBlockedDates for instant calendar feedback.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { hostingService } from '../services/hosting.service'
import { bookingKeys } from './use-booking'
import type { BlockedDateRange, SetBlockedDatesInput } from '../types/hosting.types'

// ─── Query Keys ─────────────────────────────────────────────────────────────

const hostingKeys = {
  all: ['hosting'] as const,
  stats: (hostId: string, organizationId?: string | null) =>
    [...hostingKeys.all, 'stats', hostId, organizationId ?? 'personal'] as const,
  recentBookings: (hostId: string, organizationId?: string | null) =>
    [...hostingKeys.all, 'recent-bookings', hostId, organizationId ?? 'personal'] as const,
  earnings: (hostId: string, months: number) =>
    [...hostingKeys.all, 'earnings', hostId, months] as const,
  blockedDates: (vehicleId: string, start: string, end: string) =>
    [...hostingKeys.all, 'blocked-dates', vehicleId, start, end] as const,
}

// ─── Host Stats ─────────────────────────────────────────────────────────────

export function useHostStats(hostId: string, organizationId?: string | null) {
  const query = useQuery({
    queryKey: hostingKeys.stats(hostId, organizationId),
    queryFn: () => hostingService.getHostStats(hostId, organizationId),
    enabled: !!hostId,
    staleTime: 2 * 60 * 1000, // 2 min — stats change with bookings
  })

  return {
    stats: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
  }
}

// ─── Recent Bookings ────────────────────────────────────────────────────────

export function useRecentBookings(hostId: string, limit = 10, organizationId?: string | null) {
  const query = useQuery({
    queryKey: hostingKeys.recentBookings(hostId, organizationId),
    queryFn: () => hostingService.getRecentBookings(hostId, limit, organizationId),
    enabled: !!hostId,
    staleTime: 1 * 60 * 1000, // 1 min — new bookings should appear quickly
  })

  return {
    bookings: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
  }
}

// ─── Earnings Summary ───────────────────────────────────────────────────────

export function useEarningsSummary(hostId: string, months = 6) {
  const query = useQuery({
    queryKey: hostingKeys.earnings(hostId, months),
    queryFn: () => hostingService.getEarningsSummary(hostId, months),
    enabled: !!hostId,
    // Global default staleTime (5 min) — historical data, changes slowly
  })

  return {
    earnings: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
  }
}

// ─── Blocked Dates (Calendar) ───────────────────────────────────────────────

// ─── Set Blocked Dates (Mutation with Optimistic Update) ────────────────────

export function useSetBlockedDates(vehicleId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (input: SetBlockedDatesInput) => hostingService.setBlockedDates(vehicleId, input),

    onMutate: async (input) => {
      const key = hostingKeys.blockedDates(vehicleId, input.windowStart, input.windowEnd)

      // Cancel in-flight queries for this window
      await queryClient.cancelQueries({ queryKey: key })

      // Snapshot previous value for rollback
      const previous = queryClient.getQueryData<BlockedDateRange[]>(key)

      // Optimistically replace with new blocked ranges
      const optimistic: BlockedDateRange[] = input.blockedRanges.map((r, i) => ({
        id: `optimistic-${i}`,
        vehicleId,
        startDate: r.startDate,
        endDate: r.endDate,
        isAvailable: false,
        reason: r.reason ?? null,
      }))
      queryClient.setQueryData(key, optimistic)

      return { previous, key }
    },

    onError: (_err, _input, context) => {
      // Roll back on error
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },

    onSettled: async () => {
      // Refetch ALL blocked dates for this vehicle (not just the mutation window)
      // Keep the action pending until the actual calendar has been refreshed.
      // Otherwise the successful toast appears above dates still painted free.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...hostingKeys.all, 'blocked-dates', vehicleId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...bookingKeys.all, 'unavailable-ranges', vehicleId],
        }),
      ])
    },
  })

  return {
    setBlockedDates: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  }
}
